import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { FormAnalyticsComparisonData } from '@/types/api';

/**
 * A headline number with its period-over-period change.
 *
 * The percentage is deliberately omitted when the previous period was zero:
 * going 0 to 5 is not "+500%", it is simply new, and the API sends null to say
 * so rather than infinity.
 */
export function StatDelta({
  title,
  comparison,
  className,
}: {
  title: string;
  comparison: FormAnalyticsComparisonData;
  className?: string;
}) {
  const { count, previous_count, delta, percent_change, previous_label } = comparison;
  const Icon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : ArrowRight;

  const tone =
    delta > 0
      ? 'text-[var(--success)]'
      : delta < 0
        ? 'text-[var(--color-foreground-muted)]'
        : 'text-[var(--color-foreground-muted)]';

  return (
    <div className={cn('rounded-xl border border-border/70 bg-card px-5 py-4', className)}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">{count}</p>

      {previous_count === 0 && count === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">Nothing yet</p>
      ) : (
        <p className={cn('mt-1 flex items-center gap-1 text-xs', tone)}>
          <Icon className="size-3.5" />
          <span className="tabular-nums">
            {delta > 0 ? '+' : ''}
            {delta}
            {percent_change !== null ? ` (${percent_change > 0 ? '+' : ''}${percent_change}%)` : ''}
          </span>
          <span className="text-[var(--color-foreground-subtle)]">vs {previous_label.toLowerCase()}</span>
        </p>
      )}
    </div>
  );
}
